/**
 * Cálculo de IVA sobre montos en pesos chilenos.
 *
 * Regla del sistema: **todo se almacena en enteros de pesos**. El CLP no tiene
 * decimales, así que no hace falta `Decimal` en la base de datos y desaparece
 * toda una clase de errores de redondeo flotante. El redondeo se hace una sola
 * vez, en el borde, y siempre con la misma función.
 *
 * Regla de negocio acordada: los montos de licitación (presupuesto oficial,
 * monto ofertado, monto adjudicado) son **netos**. El IVA se calcula aparte al
 * facturar. Así el margen se compara neto contra neto, que es lo correcto — el
 * IVA no es ingreso de la empresa, es dinero que se recauda para el Fisco.
 */
import { IVA_PCT_CHILE } from '../dominio/constantes.js';
/** Redondeo a entero de pesos. Único punto donde se redondea dinero. */
export function redondearPesos(valor) {
    if (!Number.isFinite(valor))
        return 0;
    return Math.round(valor);
}
/**
 * Calcula el IVA a partir del neto.
 *
 * @param montoNeto Base imponible en pesos.
 * @param ivaPct Tasa; usar `0` para documentos exentos.
 */
export function calcularDesdeNeto(montoNeto, ivaPct = IVA_PCT_CHILE) {
    const neto = redondearPesos(montoNeto);
    const tasa = Math.max(0, ivaPct);
    const iva = redondearPesos((neto * tasa) / 100);
    return { montoNeto: neto, ivaPct: tasa, montoIva: iva, montoTotal: neto + iva };
}
/**
 * Desglosa hacia atrás desde un monto bruto.
 *
 * Se usa al importar órdenes de compra de Mercado Público, donde a veces el PDF
 * solo trae el total. El neto se calcula primero y el IVA se obtiene por
 * diferencia — no recalculándolo — para que neto + iva dé exactamente el total
 * original y no se pierda o gane un peso por doble redondeo.
 */
export function calcularDesdeTotal(montoTotal, ivaPct = IVA_PCT_CHILE) {
    const total = redondearPesos(montoTotal);
    const tasa = Math.max(0, ivaPct);
    if (tasa === 0) {
        return { montoNeto: total, ivaPct: 0, montoIva: 0, montoTotal: total };
    }
    const neto = redondearPesos(total / (1 + tasa / 100));
    return { montoNeto: neto, ivaPct: tasa, montoIva: total - neto, montoTotal: total };
}
/**
 * Normaliza una entrada que puede venir en neto o en bruto a un desglose completo.
 *
 * Pensado para formularios e importadores donde el usuario tiene solo uno de los
 * dos montos. Si llegan ambos, manda el neto.
 */
export function normalizarMontos(entrada) {
    const tasa = entrada.exento ? 0 : (entrada.ivaPct ?? IVA_PCT_CHILE);
    if (entrada.montoNeto != null)
        return calcularDesdeNeto(entrada.montoNeto, tasa);
    if (entrada.montoTotal != null)
        return calcularDesdeTotal(entrada.montoTotal, tasa);
    return { montoNeto: 0, ivaPct: tasa, montoIva: 0, montoTotal: 0 };
}
/**
 * Reparte un monto neto en `partes` cuotas de forma que la suma dé exactamente
 * el original.
 *
 * El resto de la división se reparte de a un peso entre las primeras cuotas.
 * Sin esto, un contrato de $1.000.000 en 3 cuotas produciría tres cuotas de
 * $333.333 que suman $999.999 y dejan un peso colgando para siempre en la
 * conciliación.
 */
export function repartirEnCuotas(montoNeto, partes) {
    const total = redondearPesos(montoNeto);
    const n = Math.max(1, Math.floor(partes));
    const base = Math.floor(total / n);
    const resto = total - base * n;
    return Array.from({ length: n }, (_, i) => base + (i < resto ? 1 : 0));
}
/**
 * Margen de una operación, en pesos y en porcentaje sobre el ingreso.
 * Ambos montos deben venir netos.
 */
export function calcularMargen(ingresoNeto, costoNeto) {
    const utilidad = redondearPesos(ingresoNeto - costoNeto);
    const margenPct = ingresoNeto > 0 ? (utilidad / ingresoNeto) * 100 : null;
    return { utilidad, margenPct };
}
/**
 * Precio neto mínimo que deja el margen objetivo dado un costo esperado.
 *
 * Es la inversa de `calcularMargen`: si quiero 45% de margen y el proyecto me
 * costará $600.000, tengo que cobrar al menos $1.090.909.
 */
export function precioParaMargen(costoNeto, margenObjetivoPct) {
    const margen = Math.min(99.9, Math.max(0, margenObjetivoPct));
    return redondearPesos(costoNeto / (1 - margen / 100));
}
//# sourceMappingURL=iva.js.map